// Data layer for the public community directory, with dual-run fallback to the
// hardcoded maps. While /public/communities is unavailable (or returns nothing)
// the UI behaves exactly as before; once the backend serves it, the DB wins.
import { useEffect, useMemo } from "react";

import { useQuery } from "@tanstack/react-query";

import { CommunityPublic, fetchCommunityDirectory } from "@/api/communities";
import {
  IPoliceServiceConfiguration,
  ISSOConfiguration,
  serviceConfigurations as hardcodedServiceConfigurations,
  setApiServiceConfigOverride,
  ssoConfigurations as hardcodedSsoConfigurations,
} from "@/common/serviceConfigurations";
import { policeServiceNameImage as hardcodedPoliceServiceNameImage } from "@/components/alerts/add/DisplayAlert/utils";

export function useCommunitiesQuery() {
  return useQuery(["public-communities"], fetchCommunityDirectory, {
    staleTime: 60 * 60 * 1000,
    retry: 1,
    refetchOnWindowFocus: false,
  });
}

export function useCommunities() {
  return useCommunitiesQuery().data?.communities ?? [];
}

// A relative path (e.g. legacy logo_url "/service-logos/...") is portal-bundle
// specific; only an absolute URL is a real S3/CDN asset. Prefer hosted, else the
// existing (hardcoded) logo, which is itself a working portal path.
function hostedUrl(u?: string | null): string | undefined {
  return u && /^https?:\/\//i.test(u) ? u : undefined;
}

/**
 * SSO directory for the login screen. MERGES the hardcoded curated list with the
 * DB directory (keyed by name, DB overlays), rather than replacing it — so orgs
 * not yet present in the DB (or carrying no sso_slug, like the fullName-only
 * hardcoded entries) are never dropped from the dropdown. Falls back entirely to
 * the hardcoded list until /public/communities responds.
 */
export function useSsoConfigurations(): ISSOConfiguration[] {
  const communities = useCommunities();
  return useMemo(() => {
    if (communities.length === 0) return hardcodedSsoConfigurations;
    const byName = new Map<string, ISSOConfiguration>();
    for (const s of hardcodedSsoConfigurations) byName.set(s.fullName, s);
    for (const c of communities) {
      // A DB org with no sso_slug adds nothing new; leave any hardcoded entry
      // (incl. fullName-only ones) untouched.
      if (!c.sso_slug) continue;
      const existing = byName.get(c.title);
      const apiLogo = c.branding?.login_logo?.url ?? c.branding?.crest?.url;
      byName.set(c.title, {
        fullName: c.title,
        logoURL:
          hostedUrl(apiLogo) ?? existing?.logoURL ?? apiLogo ?? undefined,
        ssoSlug: c.sso_slug ?? undefined,
        ssoProvider: c.sso_provider ?? existing?.ssoProvider,
      });
    }
    return [...byName.values()].sort((a, b) =>
      a.fullName.localeCompare(b.fullName),
    );
  }, [communities]);
}

// API community -> IPoliceServiceConfiguration, merged over the hardcoded entry
// so fields this public projection doesn't carry keep their value. Note
// fieldConfigurations/units/regionFilterOptions/citizenEngage flags now live in
// rv2_org_config (admin-editable) but are served via the org-config endpoints,
// not the public list — their consumers still read the hardcoded base here
// until they're flipped to org config.
function communityToServiceConfig(
  c: CommunityPublic,
  base?: IPoliceServiceConfiguration,
): IPoliceServiceConfiguration {
  return {
    ...(base ?? ({} as IPoliceServiceConfiguration)),
    id: c.id,
    // Per-environment DB returns this env's UUID; the test/prod split dissolves.
    testID: base?.testID ?? c.id,
    fullName: c.title,
    shortName: c.short_title ?? base?.shortName,
    region: c.region_label ?? base?.region ?? "",
    // map_center is a loosely-typed JSON object on the generated type; it holds
    // { lat, lng } at runtime.
    mapCenter:
      (c.map_center as { lat: number; lng: number } | null) ??
      base?.mapCenter ??
      undefined,
    apps: (c.apps as IPoliceServiceConfiguration["apps"]) ?? base?.apps ?? [],
    logoURL: c.branding?.crest?.url ?? base?.logoURL,
    servicesURLSlug: c.services_url_slug ?? base?.servicesURLSlug,
    apiRegion: c.api_region ?? base?.apiRegion,
  };
}

function toServiceConfigurations(communities: CommunityPublic[]): {
  [key: string]: IPoliceServiceConfiguration;
} {
  const out: { [key: string]: IPoliceServiceConfiguration } = {
    ...hardcodedServiceConfigurations,
  };
  for (const c of communities) {
    const base =
      hardcodedServiceConfigurations[c.title] ??
      Object.values(hardcodedServiceConfigurations).find((s) => s.id === c.id);
    out[c.title] = communityToServiceConfig(c, base);
  }
  return out;
}

/**
 * Service configuration directory, merged API-over-hardcoded with fallback. Also
 * feeds the legacy `getServiceConfig()` accessor (via setApiServiceConfigOverride)
 * so its many call sites pick up DB data without per-site changes.
 */
export function useServiceConfigurations(): {
  [key: string]: IPoliceServiceConfiguration;
} {
  const communities = useCommunities();
  const merged = useMemo(
    () =>
      communities.length > 0
        ? toServiceConfigurations(communities)
        : hardcodedServiceConfigurations,
    [communities],
  );
  useEffect(() => {
    setApiServiceConfigOverride(communities.length > 0 ? merged : null);
  }, [communities, merged]);
  return merged;
}

/**
 * Mount once under QueryClientProvider to load the directory and populate the
 * getServiceConfig() override. Renders nothing.
 */
export function CommunitiesPreloader(): null {
  useServiceConfigurations();
  return null;
}

/**
 * Agency name -> crest image, for bulletins/alerts. Built from community crests
 * — tenants and the not-fully-onboarded reference agencies (OPP, RCMP, …), which
 * are now ordinary rubi_orgs — merged over the hardcoded policeServiceNameImage
 * so any name not yet in the DB keeps its bundled crest. Falls back entirely to
 * the hardcoded map until the API responds.
 */
export function usePoliceServiceNameImage(): { [name: string]: string } {
  const communities = useCommunities();
  return useMemo(() => {
    if (communities.length === 0) {
      return hardcodedPoliceServiceNameImage;
    }
    const out: { [name: string]: string } = {
      ...hardcodedPoliceServiceNameImage,
    };
    for (const c of communities) {
      const crest = c.branding?.crest?.url;
      if (crest) out[c.title] = crest;
    }
    return out;
  }, [communities]);
}
